import React, { useState } from "react";

import { Modal, ModalClose, ModalContent, ModalFooter, ModalHeader, useBCMSModal } from "@bcm-dev/bcms-vite";
import UIButton from "compid/ui/UIButton";
import { SVGAsset } from "res";

import "./styles.css";

const CustomModalContext = React.createContext(null);

/**
 * @function CustomModal
 * @param {STRING} className - The class to be applied to the modal component
 * @param {STRING} id - The id of the modal
 * @param {STRING} title - The title of the modal to be shown in the header
 * @returns {React.Component}
 * @exported true
 * @description Main modal component, wraps all modal content
 * @example
 * function TestModal(props) {
 *  return (
 *      <CustomModal id="TestModal" title={ "Testing" }>
 *          <CustomModalContent>
 *              ...
 *          </CustomModalContent>
 * 
 *          <CustomModalFooter>
 *              ...
 *          </CustomModalFooter>
 *      </CustomModal>
 *  );
 * }
 */
export default function CustomModal(props) {
    const className = props.className ?? "";
    const id = props.id ?? "";

    const [ titleState, setTitleState ] = useState(props.title ?? "[Modal Title]");

    return (
        <CustomModalContext.Provider value={{ titleState, setTitleState }}>
            <Modal className={ `custom-modal ${ className }` }>
                <ModalHeader className="flex-container flex-horizontal">
                    <h3 className="flex-width">{ titleState }</h3>
                    <div className="flex-width text-right">
                        <ModalClose id={ id } className="clickable">
                            <SVGAsset id="XOutlineIcon" className="icon" />
                        </ModalClose>
                    </div> 
                </ModalHeader>

                { props.children }
            </Modal>
        </CustomModalContext.Provider>
    );
}

export const useCustomModal = () => React.useContext(CustomModalContext);

/**
 * @function CustomModalContent
 * @param {STRING} className - The classname to be applied to the content
 * @returns {React.Component}
 * @exported true
 * @description Modal Content component
 * @example
 * <CustomModalContent>
 *  ...
 * </CustomModalContent>
 */
export function CustomModalContent(props) {
    const className = props.className ?? "";

    return (
        <ModalContent className={ className }>
            { props.children }
        </ModalContent>
    );
}

/**
 * @function CustmModalFooter
 * @param {STRING} className - The class that should be applied to the footer
 * @returns {React.Component}
 * @exported true
 * @description The Custom modal footer component
 * @example
 * <CustomModalFooter>
 *  ...
 * </CustomModalFooter>
 */
export function CustomModalFooter(props) {
    const className = props.className ?? "";

    return (
        <ModalFooter className={ `flex-container flex-horizontal ${ className }` }>
            { props.children }
        </ModalFooter>
    );
}

/**
 * @function ModalSecondaryButton
 * @param {STRING} text - The text of the button
 * @param {FUNCTION} onClick - The function that should occur on click
 * @param {STRING} className - The class that should be added to the button
 * @returns {React.Component}
 * @exported true
 * @description The secondary button used in a modal
 * @example
 * <ModalSecondaryButton text="SOMETHING" className="some-class" onClick={ fn } />
 */
export function ModalSecondaryButton(props) {
    const text = props.text ?? "";
    const onClick = props.onClick ?? null;
    const className = props.className ?? "";

    return (
        <UIButton className={ `secondary-button ${ className }` } onClick={ onClick }>
            { text }
        </UIButton>
    );
}

/**
 * @function ModalPrimaryButton
 * @param {STRING} text - The text of the button
 * @param {FUNCTION} onClick - The function that should occur on click
 * @param {STRING} className - The class that should be added to the button
 * @returns {React.Component}
 * @exported true
 * @description The primary button used in a modal
 * @example
 * <ModalPrimaryButton text="SOMETHING" className="some-class" onClick={ fn } />
 */
export function ModalPrimaryButton(props) {
    const text = props.text ?? "";
    const onClick = props.onClick ?? null;
    const className = props.className ?? "";

    return (
        <UIButton className={ `primary-button ${ className }` } onClick={ onClick } dataPoint={ props.dataPoint }>
            { text }
        </UIButton>
    );
}

/**
 * @function ModalCloseButton
 * @param {STRING} text - The text of the button
 * @param {STRING} id - The id of the modal
 * @param {STRING} className - The class that should be added to the button
 * @returns {React.Component}
 * @exported true
 * @description A button to close the modal
 * @example
 * <ModalCloseButton text="SOMETHING" className="some-class" id="TestModal" />
 */
export function ModalCloseButton(props) {
    const { close } = useBCMSModal();

    const id = props.id ?? "";
    const text = props.text ?? "Cancel";
    const className = props.className ?? "";

    return (
        <UIButton className={ `secondary-button ${ className }` } onClick={ () => close(id) }>
            { text }
        </UIButton>
    );
}